import { memo, useCallback, useMemo, useState } from 'react';

import { ChevronDownIcon, LayoutGridIcon, PlusIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { DNDBoardRootDrop } from '~/components/v2/components/shared/dnd/DNDBoardRootDrop';
import { SubSideNavSectionHeader } from '~/components/v2/components/workspace/sub-side-nav/SubSideNavSectionHeader';
import { WorkspaceSidebarSectionHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionHeader';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { WorkspaceSidebarWorkspaceBoardsMoreDropdown } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarWorkspaceBoardsMoreDropdown';
import { BOARDS_SELECT_EMPTY_OPTION } from '~/constants/BoardSearch';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { canChangeWorkspaceDetails } from '~/utils/permissions/workspacePermissions';

const CREATE_BOARD_PLUS_BTN_TOOLTIP = 'Create new board';

export const WorkspaceSidebarWorkspaceBoardsHeader = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapse = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canCreateRootBoards = canCreateBoard(permissions);
  const canUpdateSubnavSort = canChangeWorkspaceDetails(permissions);
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const handleCreateRootBoard = useCallback(() => {
    showCreateBoardModal({
      selectedParentBoard: BOARDS_SELECT_EMPTY_OPTION,
      trackLocation: 'side-nav-extension',
    });
  }, [showCreateBoardModal]);

  const actions = useMemo(
    () => (
      <div className="group/workspace-sidebar-workspace-boards-header flex items-center gap-1">
        {canUpdateSubnavSort && <WorkspaceSidebarWorkspaceBoardsMoreDropdown onOpenChange={setIsMoreMenuOpen} />}
        {canCreateRootBoards && (
          <Tooltip label={CREATE_BOARD_PLUS_BTN_TOOLTIP} side="right">
            <IconButton
              adsIcon
              icon={PlusIcon}
              onClick={handleCreateRootBoard}
              size="small"
              color="gray"
              appearance="ghost"
              data-testid="WORKSPACE_SIDEBAR_CREATE_NEW_BOARD_BUTTON"
              label={CREATE_BOARD_PLUS_BTN_TOOLTIP}
            />
          </Tooltip>
        )}
      </div>
    ),
    [canCreateRootBoards, canUpdateSubnavSort, handleCreateRootBoard],
  );

  const header = useMemo(
    () =>
      ui2026 ? (
        <SubSideNavSectionHeader
          actions={actions}
          collapsed={collapsed}
          data-testid="LIBRARIES_SUB_SIDE_NAV_BOARDS_HEADER"
          icon={LayoutGridIcon}
          isActionsVisible={isMoreMenuOpen}
          isRootDropTarget
          label="Boards"
          onToggle={toggleCollapse}
        />
      ) : (
        <WorkspaceSidebarSectionHeader>
          <DNDBoardRootDrop asChild>
            <Button
              appearance="ghost"
              className="ads:text-metadata text-gray-9 ads:hover:bg-hover-subtle hover:bg-gray-5 data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 ads:data-[dnd-over=true]:data-[dnd-droppable=true]:ring-primary data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-inset ads:data-[active=true]:bg-hover-subtle data-[active=true]:bg-gray-5"
              color="gray"
              data-testid="WORKSPACE_SIDEBAR_WORKSPACE_BOARDS_SECTION_HEADER"
              onClick={toggleCollapse}
              size="small"
              suffix={<ChevronDownIcon className={cn('transition-transform', collapsed && '-rotate-90')} size="sm" />}
            >
              Boards
            </Button>
          </DNDBoardRootDrop>
          {actions}
        </WorkspaceSidebarSectionHeader>
      ),
    [actions, collapsed, isMoreMenuOpen, toggleCollapse, ui2026],
  );

  return header;
});

WorkspaceSidebarWorkspaceBoardsHeader.displayName = 'WorkspaceSidebarWorkspaceBoardsHeader';
